import type { IntlShape } from 'react-intl';
import { useIntl as formatJsUseIntl } from 'react-intl';

import { useDependencies } from '@/scopes/common/infrastructure/dependencies';
import { useCommonDependency } from '@/scopes/common/infrastructure/dependencies/common';
import type { Currency } from '@/scopes/common/domain/types';

type Intl = IntlShape & {
  formatCurrency: (value: number, showSign?: boolean) => string;
  formatTruncatedCurrency: (value: number) => string;
  formatRelativeDate: (value: Date) => string;
  defaultCurrency: Currency;
};

export const useIntl = (): Intl => {
  const relativePastTimeResolver = useCommonDependency('relativePastTimeResolver');
  const { currencyResolver } = useDependencies();
  const intl = formatJsUseIntl();

  const defaultCurrency = currencyResolver.resolveDefaultForLocale(intl.locale);

  const formatCurrency = (value: number, showSign?: boolean) => {
    if (defaultCurrency === 'SEK') {
      const currency = intl.formatNumber(value, {
        style: 'decimal',
        currency: defaultCurrency,
        signDisplay: showSign ? 'always' : 'never',
      });

      return `${currency}:-`;
    }

    return intl.formatNumber(value, {
      style: 'currency',
      currency: defaultCurrency,
      signDisplay: showSign ? 'always' : 'never',
    });
  };

  const formatTruncatedCurrency = (value: number) => {
    if (defaultCurrency === 'SEK') {
      const currency = intl.formatNumber(value, {
        style: 'decimal',
        currency: defaultCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

      return `${currency}:-`;
    }

    return intl.formatNumber(value, {
      style: 'currency',
      currency: defaultCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const formatRelativeDate = (value: Date) => {
    const { daysAgo, weeksAgo, monthsAgo } = relativePastTimeResolver.resolve(value);

    if (monthsAgo) {
      return intl.formatDate(value, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }

    if (weeksAgo) {
      return intl.formatRelativeTime(-weeksAgo, 'weeks', { numeric: 'auto' });
    }

    if (daysAgo) {
      return intl.formatRelativeTime(-daysAgo, 'day', { numeric: 'auto' });
    }

    return intl.formatDate(value, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return {
    ...intl,
    formatCurrency,
    formatTruncatedCurrency,
    formatRelativeDate,
    defaultCurrency,
  };
};
